import { Component, Element, Event, type EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { makeRandomId } from '../../utils/utils';
import { BayWaSelectChanged, BaywaSelectModify, BaywaSelectOption } from '../../interfaces/select';

@Component({
  tag: 'baywa-select',
  styleUrl: 'baywa-select.scss',
  scoped: false,
})
export class BaywaSelect {
  private animation: number;
  private animationSpeed: number = 200;
  private delay: number = 50;
  private id: string;
  private inputEl!: HTMLInputElement;
  private isClicked: boolean;
  private multiple: boolean = false;
  private optionList: HTMLBaywaSelectOptionsElement | null;
  private options: HTMLOptionsCollection;
  private selectEl: HTMLSelectElement | null = null;

  @Element() el: HTMLElement;

  @State() customOptionsState: Array<BaywaSelectOption>;
  @State() disabled: boolean = false;
  @State() isListOpen: boolean = false;
  @State() selectedOptions: NodeListOf<HTMLOptionElement>;
  @State() selectedValues: Array<string>;

  /**
   * If set, show chips under the dropdown.
   */
  @Prop() chips: boolean = false;
  /**
   * Custom option data.
   */
  @Prop() customOptions: BaywaSelectOption[];
  /**
   * Optional ID for receiving remote requests to update the selection
   */
  @Prop() groupId: string;
  /**
   * Optional attribute for selectors that the other selectors should mirror when it changes
   */
  @Prop() groupLead: boolean = false;
  /**
   * The ID for the input. Can be set, otherwise one will be randomly assigned
   */
  @Prop({ attribute: 'id' }) inputId: string;
  /**
   * first option is selected
   */
  @Prop({ mutable: true }) isFirst: boolean = false;
  /**
   * last option is selected
   */
  @Prop({ mutable: true }) isLast: boolean = false;
  /**
   * The select label
   */
  @Prop() label: string;
  /**
   * The maximum height of the open select box, in px
   */
  @Prop() maxHeight: number = 308;
  /**
   * The text set within the input's placeholder attribute
   */
  @Prop() placeholder: string;
  /**
   * If set, use the search field functionality
   */
  @Prop() searchable: boolean = false;
  /**
   * If set, broadcast to its twins the new value
   */
  @Prop() sync: boolean = false;

  @Watch('customOptions')
  updateCustomOptionsState() {
    this.customOptionsState = this.customOptions;
  }

  /**
   * Fired when selection has changed
   */
  @Event({
    composed: true,
    bubbles: true,
    cancelable: false,
  })
  selectionChanged: EventEmitter<BayWaSelectChanged>;

  /**
   * Custom event to send if other selectors should follow this one's lead
   */
  @Event({
    composed: true,
    bubbles: true,
    cancelable: false,
  })
  updateSelectors: EventEmitter<BaywaSelectModify>;

  @Listen('updateSelectors', { target: 'document' })
  onUpdateSelectors(event: CustomEvent<BaywaSelectModify>) {
    // react to the new value only if:
    // * the group ID matches, and this selector is not the group lead
    // * the sync attribute is set and the groupID matches, but the ID differs
    // * the sync attribute is not set, and the ID matches
    const followsTheLeader = !this.sync && !this.groupLead && event.detail.groupId == this.groupId;
    const syncsWithPeer = this.sync && event.detail.groupId == this.groupId && event.detail.id != this.id;
    const justThisId = !this.sync && !event.detail.groupId && event.detail.id == this.id;
    if (event.detail.values != this.selectedValues && (followsTheLeader || syncsWithPeer || justThisId)) {
      this.updateSelectedOptions(event.detail.values, true);
    }
  }

  componentWillLoad() {
    this.selectEl = this.el.querySelector('select');
    if (this.selectEl) {
      this.options = this.selectEl.options;
      this.multiple = this.selectEl.multiple;
      this.disabled = this.selectEl.disabled;
      if (this.selectEl.selectedOptions.length <= 0) {
        if (this.placeholder) {
          const placeholder = document.createRange().createContextualFragment(`<option value="" disabled hidden selected>${this.placeholder}</option>`);
          this.selectEl.insertBefore(placeholder, this.selectEl.querySelector('option'));
        } else {
          const defaultSelect = this.selectEl.querySelector('option');
          if (defaultSelect) {
            defaultSelect.selected = true;
          }
        }
      }
      if (this.inputId) {
        this.id = this.inputId;
      } else if (this.selectEl?.id) {
        this.id = this.selectEl.id;
      } else {
        this.id = makeRandomId('select');
      }
      this.handleOptionSelected();
      this.selectEl.addEventListener('change', this.handleOptionSelected);
    }
  }

  disconnectedCallback() {
    this.optionList?.remove();
    this.optionList = null;
  }

  /**
   * Advances to the next option in the select element.
   * Wraps around to the first option if the end is reached.
   */
  @Method()
  async next(): Promise<void> {
    let newSelectedIndex = this.getSelectedIndex() + 1;
    if (newSelectedIndex > this.options.length) {
      newSelectedIndex = 0;
    }
    if (this.selectEl) this.selectEl.selectedIndex = newSelectedIndex;
    this.handleOptionSelected();
  }
  /**
   * Moves to the previous option in the select element.
   * Wraps around to the last option if the beginning is reached.
   */
  @Method()
  async previous(): Promise<void> {
    let newSelectedIndex = this.getSelectedIndex() - 1;
    if (newSelectedIndex < 0) {
      newSelectedIndex = this.options.length;
    }
    if (this.selectEl) this.selectEl.selectedIndex = newSelectedIndex;
    this.handleOptionSelected();
  }

  private closeOptionList() {
    window.clearTimeout(this.animation);
    if (this.optionList) {
      this.optionList.classList.remove('open');
    }
    this.animation = window.setTimeout(() => {
      this.optionList?.remove();
      this.optionList = null;
      this.isListOpen = false;
    }, this.animationSpeed);
  }

  private emitSelectionChangedEvent() {
    this.selectedValues = Array.from(this.selectedOptions).map(({ value }) => value);
    const eventDetail: BayWaSelectChanged = {
      values: this.selectedValues,
      isFirst: this.isFirst,
      isLast: this.isLast,
    };
    this.selectionChanged.emit(eventDetail);
  }

  private getSelectedIndex(): number {
    if (this.selectEl) return this.selectEl.selectedIndex;
    return -1;
  }

  private getSelectedOptions(): string {
    const selOptions: HTMLOptionElement[] = Array.from(this.selectedOptions);
    return selOptions.length > 0 ? selOptions.map(({ text }) => text).join(', ') : '';
  }

  private handleOptionSelected() {
    this.updateSelectedOptions();
    this.emitSelectionChangedEvent();
  }

  private handleInputBlur() {
    this.isClicked = false;
  }

  private onInputClick() {
    this.isClicked = true;
    if (this.isListOpen) {
      this.closeOptionList();
    } else {
      this.showOptionList();
    }
  }

  private onInputFocus() {
    if (!this.isClicked) {
      this.showOptionList();
    }
    this.isClicked = false;
  }

  private removeChipOption(option: HTMLOptionElement) {
    option.selected = false;
    this.updateSelectedOptions();
  }

  private renderChips() {
    return (
      this.multiple &&
      this.chips && (
        <div class="chip-container">
          {Array.from(this.selectedOptions).map(opt => (
            <baywa-chip
              key={opt.text}
              icon={opt.dataset.icon}
              label={opt.text}
              onChipClose={() => {
                this.removeChipOption(opt);
              }}
            ></baywa-chip>
          ))}
        </div>
      )
    );
  }

  private renderInput() {
    return (
      <div class={{ 'select-input': true, 'disabled': this.disabled, 'open': this.isListOpen }}>
        <baywa-input
          disabled={this.disabled}
          id={this.inputId + '-input'}
          label={this.label}
          onInputBlur={() => {
            this.handleInputBlur();
          }}
          onInputFocus={() => {
            this.onInputFocus();
          }}
          onInputMouseDown={() => {
            this.onInputClick();
          }}
          onInputRef={({ detail: el }) => (this.inputEl = el)}
          placeholder={this.placeholder}
          readonly={!this.disabled}
          truncate={true}
          value={this.getSelectedOptions()}
        ></baywa-input>
        <div class="arrow-icon">
          <baywa-icon icon="icon-ebusiness-select-arrow"></baywa-icon>
        </div>
      </div>
    );
  }

  private showOptionList() {
    window.clearTimeout(this.animation);
    if (this.isListOpen) {
      return;
    }
    this.isListOpen = true;

    this.optionList = document.createElement('baywa-select-options');
    this.optionList.highlight = this.getSelectedIndex();
    this.optionList.opener = this.inputEl;
    this.optionList.options = this.options;
    this.optionList.searchable = this.searchable;
    this.optionList.multiple = this.multiple;
    this.optionList.indexOffset = this.placeholder ? -1 : 0;
    this.optionList.addEventListener('optionSelected', () => {
      this.handleOptionSelected();
    });
    this.optionList.addEventListener('listClose', () => {
      this.closeOptionList();
    });

    this.setOptionsListPosition();

    document.body.appendChild(this.optionList);

    // when class open is set immediately it has no effect
    this.animation = window.setTimeout(() => {
      this.optionList?.classList.add('open');
    }, this.delay);
  }

  private setOptionsListPosition() {
    const { left, width, bottom } = this.inputEl.getBoundingClientRect();
    if (!this.optionList) return;
    this.optionList.style.top = `${bottom + window.scrollY - 1}px`;
    this.optionList.style.left = `${left + window.scrollX}px`;
    this.optionList.style.width = `${width}px`;
  }

  private updateSelectedOptions(values: string[] = [], received = false) {
    if (this.selectEl) {
      const options = this.selectEl.querySelectorAll('option');
      if (values.length > 0) {
        options.forEach(option => {
          option.selected = values.indexOf(option.value) > -1;
        });
      }
      const selectedIndex = this.getSelectedIndex();
      this.selectedOptions = this.selectEl.querySelectorAll('option:not([hidden]):checked');
      const selectedValues: Array<string> = [];
      this.selectedOptions.forEach(option => {
        selectedValues.push(option.value);
      });
      this.isFirst = selectedIndex === 0;
      this.isLast = selectedIndex === this.selectEl.options.length;
      if (this.groupId && (this.groupLead || this.sync) && !received) {
        this.updateSelectors.emit({
          id: this.id,
          groupId: this.groupId,
          values: selectedValues,
        });
      }
    }
  }

  render() {
    return this.selectEl ? (
      <Host class="baywa-select">
        {this.renderInput()}
        <slot />
        {this.renderChips()}
      </Host>
    ) : null;
  }
}
